import React, { forwardRef } from 'react';
import { Tab, makeStyles, shorthands } from '@fluentui/react-components';
import type { PopoverTriggerChildProps } from '@fluentui/react-components';
import { SideNavigationItemProps, SideNavigationConfigProps } from './types';
import { SideNavigationModes } from './side-navigation-mode';
import { defaultConfig, getTabClassNames } from './utilities';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
  },
  hrefAnchor: {
    ...shorthands.textDecoration('none')
  }
});

export type SideNavigationTabItemProps = {
  item: SideNavigationItemProps,
  mode: SideNavigationModes,
  className: string,
  config: SideNavigationConfigProps,
} & Partial<PopoverTriggerChildProps>;

export const SideNavigationItemTab = forwardRef<HTMLButtonElement, SideNavigationTabItemProps>((props, ref) => {
  const {
    item,
    config,
    mode,
    className,
  } = props;
  const {
    icon: Icon,
    id,
    text,
    onClick,
    key,
    href
  } = item;

  const {
    isTemporarySelected = defaultConfig.isTemporarySelected, getAccessoryItem = defaultConfig.getAccessoryItem,
  } = config;

  const temporarySelected = isTemporarySelected(item);
  const styles = useStyles();
  const content = mode === SideNavigationModes.IconOnly
    ? undefined
    : <span className="side-navigation-item-text">{text}</span>;
  const resolvedIcon = mode === SideNavigationModes.TextOnly || !Icon ? undefined : <Icon />;
  const classes = getTabClassNames(item, temporarySelected, className);
  const accessoryItem = getAccessoryItem(item);

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button === 0 && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const tab = (
    <div key={`div_tab_${key}`} className={styles.container}>
      <Tab
        onClick={href ? undefined : onClick}
        {...props}
        ref={ref}
        key={`tab_${key}`}
        value={id}
        className={classes}
        icon={resolvedIcon}
        role="tab"
        aria-label={text}
        id={id}
      >
        {content}
      </Tab>
      {accessoryItem}
    </div>
  );

 return href ? (
  <a
    href={href}
    className={styles.hrefAnchor}
    onClick={handleAnchorClick}
    onAuxClick={(e) => e.button === 1 && e.preventDefault()}
  >
    {tab}
  </a>
) : (
  tab
);
});
